import {
  HttpClient,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  firstValueFrom,
  from,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { hasSessionHint, readCookie } from './cookie.util';
import { AliasSessionService } from '../services/alias-session.service';
import { StepUpService } from '../services/step-up.service';

// connect_-prefixed to match the backend AuthCookieFactory cookie names (namespaced so they can't
// collide with a same-named cookie another service sets on a shared host).
const CSRF_COOKIE = 'connect_csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Set by the backend when the alias SESSION itself is gone (see
// alias.interceptor.ts, which owns the clear + toast + hard-reload for this
// case). A local copy is kept here — mirroring how the two interceptors each
// hold their own header/cookie constants — so this interceptor can recognize a
// session-ended 401 and decline to refresh/retry it.
const ALIAS_SESSION_ENDED_HEADER = 'X-Alias-Session-Ended';

// Auth endpoints excluded from the 401 refresh-retry flow. Refreshing on a 401
// from these would loop forever: /user/refresh and /user/refresh-alias are the
// refreshes themselves, a 401 from /user/login or /user/logout is a terminal
// auth state, and a 401 from /user/reauth is a terminal "wrong password" signal
// that the step-up modal surfaces directly — none of these are expired-session
// signals. (Without the /user/reauth exemption, a wrong-password 401 during an
// active step-up — where the session IS valid — would trigger a refresh that
// succeeds, retry reauth, 401 again, and refresh in a tight loop.)
const REFRESH_EXEMPT_PATHS = [
  '/user/refresh',
  '/user/refresh-alias',
  '/user/login',
  '/user/logout',
  '/user/reauth',
];

// The step-up re-auth endpoint. Excluded from the 403 STEP_UP branch so a
// step-up response on reauth itself can never recurse into another modal.
const REAUTH_PATH = '/user/reauth';

// Single-flight refresh state, shared across every request that hits a 401.
// Concurrent 401s do not each fire their own refresh: the first one performs
// the POST /user/refresh and the rest wait on `refreshOutcome$` for its result.
let refreshInProgress = false;
// null = no result yet (refresh in flight); true = refresh succeeded;
// false = refresh failed (session is being torn down).
const refreshOutcome$ = new BehaviorSubject<boolean | null>(null);

/**
 * Root functional auth interceptor.
 *
 * - Sends cookies on every request (`withCredentials`) so the httpOnly access /
 *   refresh cookies travel cross-origin to the API.
 * - Echoes the readable `csrf_token` cookie as `X-CSRF-Token` on state-changing
 *   requests (double-submit CSRF).
 * - On a 401, performs a single shared refresh and retries the original
 *   request once; if the refresh fails, hard-logs-out. During a staff alias
 *   (read-only guidance) session the alias access token has a shorter TTL than
 *   the session, so a plain 401 there refreshes the ALIAS token via
 *   `POST /user/refresh-alias` (NOT the staff `POST /user/refresh`, which would
 *   rotate the wrong token and leave the retry still failing).
 * - On a 403 `{ code: STEP_UP_REQUIRED }`, opens the single step-up re-auth
 *   modal (via StepUpService) and retries the original request once on success.
 *   Other 403s (including CSRF) propagate unchanged.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // inject() must run synchronously within the interceptor's injection context;
  // capture the dependencies here so the async rxjs callbacks can reuse them.
  const http = inject(HttpClient);
  const aliasSession = inject(AliasSessionService);
  const stepUp = inject(StepUpService);

  return next(applyAuth(req)).pipe(
    catchError((error: HttpErrorResponse) => {
      // A session-ended 401 is owned entirely by aliasInterceptor (clear +
      // toast + hard-reload back to the staffer). Never refresh/retry it here:
      // the alias session is gone, so there is nothing to refresh into, and a
      // staff refresh + retry would briefly run as the write-capable staffer
      // with the alias cookies already cleared (racy). Checked first and
      // unconditionally so no later branch can act on it.
      if (
        error.status === 401 &&
        error.headers.get(ALIAS_SESSION_ENDED_HEADER) === 'true'
      ) {
        return throwError(() => error);
      }
      // Only attempt refresh/retry when the SPA believes it has a session (see hasSessionHint()).
      // A 401 with no hint means we're simply not logged in (e.g. on the login page) — propagate it
      // rather than refreshing + hard-logging-out, which would reload the page and loop: an
      // unauthenticated cold-start request (e.g. APP_INITIALIZER fetching ui-secrets) would go
      // 401 -> refresh fails -> hard-logout -> full reload -> 401 -> ...
      if (
        error.status === 401 &&
        !isRefreshExempt(req.url) &&
        hasSessionHint()
      ) {
        // In an alias session refresh the alias token; otherwise the staff token.
        return handle401(req, next, http, error, aliasSession.isAliased());
      }
      // Step-up gate: the backend rejects a sensitive request that needs fresh
      // re-auth with 403 { code: STEP_UP_REQUIRED }. Trigger the re-auth modal
      // and, on success, retry the original request once. This MUST key on the
      // `code` — a missing/invalid CSRF token also returns 403 but with a
      // different body, and firing step-up on bare 403 status would loop.
      if (
        error.status === 403 &&
        error.error?.code === 'STEP_UP_REQUIRED' &&
        !isReauthRequest(req.url)
      ) {
        return handleStepUp(req, next, stepUp, error);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Clones the request with credentials enabled and, for state-changing methods,
 * the CSRF header read from the readable `csrf_token` cookie.
 */
function applyAuth(req: HttpRequest<unknown>): HttpRequest<unknown> {
  let headers = req.headers;
  if (STATE_CHANGING_METHODS.has(req.method.toUpperCase())) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      headers = headers.set(CSRF_HEADER, csrf);
    }
  }
  return req.clone({ withCredentials: true, headers });
}

function isRefreshExempt(url: string): boolean {
  return REFRESH_EXEMPT_PATHS.some((path) => url.includes(path));
}

function isReauthRequest(url: string): boolean {
  return url.includes(REAUTH_PATH);
}

/**
 * Handles a 403 STEP_UP_REQUIRED by opening the re-auth modal (via the shared
 * StepUpService) and retrying the original request once on success. Concurrent
 * STEP_UP responses share ONE modal: StepUpService single-flights the flow, so
 * every waiting request receives the same outcome — all retry, or all fail.
 * On cancel the original 403 propagates unchanged (no retry, no loop).
 */
function handleStepUp(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  stepUp: StepUpService,
  originalError: HttpErrorResponse
): Observable<any> {
  return stepUp.require().pipe(
    switchMap((reauthed) =>
      reauthed
        ? next(applyAuth(req)) // rebuild auth (fresh CSRF cookie) and retry once
        : throwError(() => originalError)
    )
  );
}

/**
 * Handles a 401 with a single-flight refresh followed by one retry of the
 * original request. The first 401 in a burst runs the refresh; subsequent
 * concurrent 401s wait for that same refresh to resolve.
 */
function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  http: HttpClient,
  originalError: HttpErrorResponse,
  aliased: boolean
): Observable<any> {
  if (!refreshInProgress) {
    refreshInProgress = true;
    refreshOutcome$.next(null);

    return performRefresh(http, aliased)
      .pipe(
        switchMap(() => {
          refreshInProgress = false;
          refreshOutcome$.next(true);
          // Rebuild auth (picks up the rotated CSRF cookie) and retry once.
          return next(applyAuth(req));
        }),
        catchError(() => {
          refreshInProgress = false;
          refreshOutcome$.next(false);
          // A failed ALIAS refresh means the alias session is gone; the
          // session-ended 401 from /user/refresh-alias is already handled by
          // aliasInterceptor (clear + toast + hard-reload to the staffer), so
          // just propagate rather than running the staff hard-logout — that
          // would wrongly bounce the underlying staffer to /login.
          return aliased
            ? throwError(() => originalError)
            : hardLogout(http, originalError);
        })
      );
  }

  // A refresh is already in flight — wait for its outcome, then retry once or
  // fail out (the in-flight refresh owns the hard-logout on failure).
  return refreshOutcome$.pipe(
    filter((outcome): outcome is boolean => outcome !== null),
    take(1),
    switchMap((succeeded) =>
      succeeded ? next(applyAuth(req)) : throwError(() => originalError)
    )
  );
}

/**
 * Runs POST /user/refresh under a CROSS-TAB lock so at most one tab in the origin
 * rotates the shared refresh cookie at a time. The single-flight state above is
 * per-tab (module-level), so without this two tabs could each refresh with the
 * same rotating token concurrently; the backend's single-use reuse-detection would
 * then revoke the whole chain and force-log-out an innocent multi-tab user. The Web
 * Locks API serializes refresh across tabs — a tab that acquires the lock refreshes
 * against the latest cookie (a prior holder's rotation is already in the shared
 * cookie jar), so no reuse is detected. Falls back to an unlocked refresh where Web
 * Locks is unavailable (older browsers / insecure context).
 *
 * During an alias session (`aliased`) this refreshes the ALIAS access token via
 * `/user/refresh-alias` (which resolves the alias_refresh_token cookie and
 * re-issues a fresh alias access token for the SAME session); otherwise it runs
 * the staff `/user/refresh`. Both are refresh-exempt, so a failing refresh can
 * never re-enter this flow and loop.
 */
function performRefresh(http: HttpClient, aliased: boolean): Observable<unknown> {
  const endpoint = aliased ? '/user/refresh-alias' : '/user/refresh';
  const doRefresh = () =>
    http.post(environment.apiUrl + endpoint, {}, { withCredentials: true });

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) {
    return doRefresh();
  }
  return from(locks.request('connect-token-refresh', () => firstValueFrom(doRefresh())));
}

/**
 * Best-effort server-side logout, then client-state teardown and a FULL document load to the login
 * screen. The full reload (not an in-app router navigation) is deliberate: it reinitializes the whole
 * Angular app so every root-singleton service's in-memory cache is destroyed. Otherwise a service that
 * buffers the signed-in user's data in a ReplaySubject/BehaviorSubject (e.g. ProjectService) would
 * retain it, and the next user to sign in on this tab would see the previous user's data.
 */
function hardLogout(
  http: HttpClient,
  originalError: HttpErrorResponse
): Observable<never> {
  return http
    .post(environment.apiUrl + '/user/logout', {}, { withCredentials: true })
    .pipe(
      catchError(() => of(null)),
      switchMap(() => {
        clearClientState();
        window.location.href = '/login';
        return throwError(() => originalError);
      })
    );
}

function clearClientState(): void {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Storage unavailable (e.g. privacy mode) — nothing to clear.
  }
}
